import { Injector, Type } from '@angular/core';

import { mergeAttributes, Node as TipTapNode } from '@tiptap/core';
import { AngularNodeViewComponent, AngularNodeViewRenderer } from 'ngx-tiptap';
import { APP_TIPTAP_EDITOR_RENDER_AS } from '../tokens';

export type ZoomJoinLinkOptions = {
    HTMLAttributes: Record<string, any>;
};
declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        zoomJoinLink: {
            insertZoomJoinLink: (options: {
                showJoinButton?: boolean;
                showJoinUrl?: boolean;
                showCopyLinkButton?: boolean;
                showCopyPasswordButton?: boolean;
                renderAs?: string;
                zoomId?: string;
                zoomTopic?: string;
            }) => ReturnType;

            updateZoomJoinLink: (options: {
                showJoinButton?: boolean;
                showJoinUrl?: boolean;
                showCopyLinkButton?: boolean;
                showCopyPasswordButton?: boolean;
                renderAs?: string;
                zoomId?: string;
                zoomTopic?: string;
            }) => ReturnType;
        };
    }
}

export const createZoomLinkExtension = (injector: Injector, component: Type<AngularNodeViewComponent>) => {
    const renderMode = injector.get(APP_TIPTAP_EDITOR_RENDER_AS);

    const renderForEmail = (attr: Record<string, any>) => {
        const renderAs = attr['renderAs'];

        if (renderAs === 'link') {
            return [`{{ zoomJoinUrl_${attr['zoomId']} | to_link }}`];
        }

        if (renderAs === 'joinButton') {
            return [
                [
                    'a',
                    {
                        href: `{{ zoomJoinUrl_${attr['zoomId']} }}`,
                    },
                    [
                        'img',
                        {
                            src: 'https://cdn.shareablelink.online/production-public/images/join-now.png',
                            alt: 'Join Now',
                        },
                    ],
                ],
                // need to add plain text link after to copy
                ['br'],
                ['br'],
                [
                    'span',
                    {},
                    `If the button above does not work, paste this into your browser: `,
                    ['a', { href: `{{ zoomJoinUrl_${attr['zoomId']} }}` }, `{{ zoomJoinUrl_${attr['zoomId']} }}`],
                ],
                ['br'],
                ['br'],
            ];
        }

        return [`{{ zoomJoinUrl_${attr['zoomId']} }}`];
    };

    return TipTapNode.create<ZoomJoinLinkOptions>({
        name: 'zoomLink',
        inline: true,
        group: 'inline',
        selectable: true,
        draggable: true,
        atom: true,

        addOptions() {
            return {
                HTMLAttributes: {},
            };
        },

        addAttributes() {
            return {
                showJoinButton: {
                    default: false,
                },
                showJoinUrl: {
                    default: true,
                },
                showCopyLinkButton: {
                    default: false,
                },
                showCopyPasswordButton: {
                    default: false,
                },
                renderAs: {
                    default: 'text',
                },
                zoomId: {
                    default: '',
                },
            };
        },

        parseHTML() {
            return [{ tag: `[data-type="${this.name}"]` }];
        },

        renderHTML({ node, HTMLAttributes }) {
            if (renderMode === 'html') {
                return [
                    'span',
                    mergeAttributes(this.options.HTMLAttributes, {
                        // ...HTMLAttributes,
                        showJoinButton: HTMLAttributes['showJoinButton'],
                        showJoinUrl: HTMLAttributes['showJoinUrl'],
                        showCopyLinkButton: HTMLAttributes['showCopyLinkButton'],
                        showCopyPasswordButton: HTMLAttributes['showCopyPasswordButton'],
                        renderAs: HTMLAttributes['renderAs'],
                        zoomId: HTMLAttributes['zoomId'],
                        zoomTopic: HTMLAttributes['zoomTopic'],

                        style: HTMLAttributes['style'],
                        'data-type': this.name,
                    }),
                ];
            }

            return [
                'span',
                mergeAttributes(this.options.HTMLAttributes, {
                    //   zoomId: HTMLAttributes['zoomId'],

                    style: HTMLAttributes['style'],
                    //   'data-type': this.name,
                }),
                ...(renderMode === 'email' ? renderForEmail(HTMLAttributes) : ['']),
            ];
        },

        addNodeView() {
            return AngularNodeViewRenderer(component, { injector });
        },

        addCommands() {
            return {
                insertZoomJoinLink:
                    options =>
                    ({ tr, dispatch, editor }) => {
                        const { selection } = tr;
                        const node = this.type.create(options);

                        if (dispatch) {
                            tr.replaceRangeWith(selection.from, selection.to, node);
                        }

                        return true;
                    },

                updateZoomJoinLink:
                    options =>
                    ({ chain, tr }) => {
                        const config: Record<string, boolean | string> = {};
                        const node = (tr as any).curSelection?.node;

                        if (!node || node.type.name !== this.name) {
                            return false;
                        }

                        const { attrs } = node;

                        if (options.showJoinButton !== undefined) {
                            config['showJoinButton'] = !(attrs['showJoinButton'] === true);
                        }

                        if (options.showJoinUrl !== undefined) {
                            config['showJoinUrl'] = !(attrs['showJoinUrl'] === true);
                        }

                        if (options.showCopyLinkButton !== undefined) {
                            config['showCopyLinkButton'] = !(attrs['showCopyLinkButton'] === true);
                        }

                        if (options.showCopyPasswordButton !== undefined) {
                            config['showCopyPasswordButton'] = !(attrs['showCopyPasswordButton'] === true);
                        }

                        if (options.zoomId !== undefined) {
                            config['zoomId'] = options.zoomId;
                        }

                        if (options.zoomTopic !== undefined) {
                            config['zoomTopic'] = options.zoomTopic;
                        }

                        if (options.renderAs !== undefined) {
                            config['renderAs'] = options.renderAs;
                        }

                        return chain().updateAttributes(this.name, config).run();
                    },
            };
        },
    });
};
